import React, { useRef, useState, useEffect, useCallback } from 'react'
import Slider, { Settings } from 'react-slick'
import cx from 'classnames'
import Link from 'next/link'
import { CAROUSEL_CARD_WIDTHS, CAROUSEL_CONFIG, CAROUSEL_ANIMATION } from '~constants'
import { useResponsiveSlidesToShow } from 'utils/carousel/useResponsiveSlidesToShow'
import { useWindowSize } from 'utils/hooks'
import styles from './CarouselV2.module.scss'
import { BreakpointKey, HeadlineType } from '~types'
import { Heading } from '~elements'
import ScrollSlider from './ScrollSlider'
import CarouselArrows from './CarouselArrows'
import { useVisibleSlidesCalculation } from '~utils/hooks/useVisibleSlidesCalculation'
/**
 * Props for CarouselV2 component
 *
 * @property useCustomSlideCalculation - If true, uses custom slide calculation logic; if false, uses responsive settings
 * @property useScrollSliderBelowBreakpoint - If provided, uses ScrollSlider component instead of react-slick below the specified breakpoint
 * @property useDotArrows - Show arrows+dots combo for navigation
 * @property sliderRef - External ref to control the slider from parent component, for example used on CoverFlowCarousel
 * @property onNavigationReady - Callback that receives navigation methods (goToNext, goToPrev, goToSlide) when carousel is ready
 */
export interface CarouselV2Props<T> {
  slides: T[]
  renderSlide: (slide: T, index: number, isTransitioning?: boolean) => React.ReactNode
  className?: string
  allShowsUrl?: string
  enableGlobalKeyboard?: boolean
  fadePeekingSlides?: boolean
  peekNextSlide?: boolean
  showAllShowsButton?: boolean
  carouselSettings?: Settings
  showArrowsBreakpointUp?: BreakpointKey | null
  headline?: HeadlineType
  useCustomSlideCalculation?: boolean
  useScrollSliderBelowBreakpoint?: BreakpointKey
  useDotArrows?: boolean
  sliderRef?: React.RefObject<Slider>
  sliderContainerClass?: string
  slideChangeDelay?: number
  onNavigationReady?: (navigation: {
    goToNext: () => void
    goToPrev: () => void
    goToSlide: (index: number) => void
  }) => void
}

const CarouselV2 = <T,>({
  slides,
  className,
  carouselSettings,
  allShowsUrl,
  fadePeekingSlides = false,
  peekNextSlide = false,
  showArrowsBreakpointUp = 'sm',
  renderSlide,
  enableGlobalKeyboard = true,
  showAllShowsButton = true,
  headline,
  useCustomSlideCalculation = false,
  useScrollSliderBelowBreakpoint,
  useDotArrows = false,
  sliderRef: externalSliderRef,
  sliderContainerClass,
  slideChangeDelay = 0,
  onNavigationReady
}: CarouselV2Props<T>) => {
  const internalSliderRef = useRef<Slider>(null)
  const sliderRef = externalSliderRef || internalSliderRef
  const containerRef = useRef<HTMLDivElement>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isAnimatingIn, setIsAnimatingIn] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  // Use dynamic slides to scroll for the awkward midpoints between
  // our target screen sizes
  const slideCount = slides?.length ?? 0

  const responsiveSlidesToShow = useResponsiveSlidesToShow(
    carouselSettings?.slidesToShow ?? 1,
    carouselSettings?.responsive ?? []
  )
  const currentCardSlidesShown = useCustomSlideCalculation ? null : responsiveSlidesToShow
  const cardCarouselHasMoreSlides = currentCardSlidesShown && slideCount > currentCardSlidesShown
  const { isBreakpoint: isBelowSm } = useWindowSize('sm', '<')
  const arrowsBreakpointCheck = useWindowSize(showArrowsBreakpointUp ?? 'sm', '>')

  const showArrowsAtBreakpoint = showArrowsBreakpointUp == null ? true : arrowsBreakpointCheck.isBreakpoint
  const shouldPeekNextSlide = peekNextSlide && cardCarouselHasMoreSlides

  const scrollSliderBreakpointCheck = useWindowSize(useScrollSliderBelowBreakpoint ?? 'sm', '<')
  const shouldUseScrollSlider = useScrollSliderBelowBreakpoint && scrollSliderBreakpointCheck.isBreakpoint

  const { visibleSlides, slidesToScroll } = useVisibleSlidesCalculation(containerRef, {
    cardWidths: CAROUSEL_CARD_WIDTHS,
    config: {
      ...CAROUSEL_CONFIG,
      SCROLL_THRESHOLD: CAROUSEL_CONFIG.SCROLL_THRESHOLD
    },
    currentCardSlidesShown: currentCardSlidesShown || undefined
  })
  // React slick settings
  const settings: Settings = {
    speed: CAROUSEL_ANIMATION.SPEED,
    slidesToScroll: slidesToScroll,
    slidesToShow: visibleSlides,
    arrows: false,
    autoplay: false,
    dots: false,
    infinite: false,
    ...carouselSettings
  }
  // Override beforeChange to track current slide
  // also call any provided callback
  settings.beforeChange = (_oldIndex: number, newIndex: number) => {
    setCurrentSlide(newIndex)
    carouselSettings?.beforeChange && carouselSettings.beforeChange(_oldIndex, newIndex)
  }

  // Handle initial animation on mount
  useEffect(() => {
    if (!hasAnimated) {
      // Small delay to ensure the component is fully mounted
      const timer = setTimeout(() => {
        setIsAnimatingIn(true)
        setHasAnimated(true)
      }, CAROUSEL_ANIMATION.ENTRANCE_DELAY)

      return () => clearTimeout(timer)
    }
  }, [hasAnimated])

  const goToNext = useCallback(() => {
    if (!sliderRef.current) return
    if (slideChangeDelay > 0 && isTransitioning) return

    if (slideChangeDelay > 0) {
      setIsTransitioning(true)
      setTimeout(() => {
        try {
          sliderRef.current?.slickNext()
        } finally {
          setIsTransitioning(false)
        }
      }, slideChangeDelay)
    } else {
      sliderRef.current.slickNext()
    }
  }, [sliderRef, slideChangeDelay, isTransitioning])

  const goToPrev = useCallback(() => {
    if (!sliderRef.current) return
    if (slideChangeDelay > 0 && isTransitioning) return

    if (slideChangeDelay > 0) {
      setIsTransitioning(true)
      setTimeout(() => {
        try {
          sliderRef.current?.slickPrev()
        } finally {
          setIsTransitioning(false)
        }
      }, slideChangeDelay)
    } else {
      sliderRef.current.slickPrev()
    }
  }, [sliderRef, slideChangeDelay, isTransitioning])

  const goToSlide = useCallback(
    (slideIndex: number) => {
      if (!sliderRef.current) return
      if (slideChangeDelay > 0 && isTransitioning) return

      if (slideChangeDelay > 0) {
        setIsTransitioning(true)
        setTimeout(() => {
          try {
            sliderRef.current?.slickGoTo(slideIndex)
          } finally {
            setIsTransitioning(false)
          }
        }, slideChangeDelay)
      } else {
        sliderRef.current.slickGoTo(slideIndex)
      }
    },
    [sliderRef, slideChangeDelay, isTransitioning]
  )

  // Expose navigation methods to parent
  useEffect(() => {
    if (onNavigationReady) {
      onNavigationReady({
        goToNext,
        goToPrev,
        goToSlide
      })
    }
  }, [onNavigationReady, goToNext, goToPrev, goToSlide])

  // Calculate if buttons should be disabled
  // Don't disable arrows if infinite scroll is enabled
  const isAtStart = settings.infinite ? false : currentSlide === 0

  // Simple end detection based on current slide and slides to scroll
  const isAtEnd = settings.infinite ? false : currentSlide + visibleSlides >= slideCount

  // Show arrows only if there are more slides than can fit on screen and prop is true
  // Don't show arrows when using ScrollSlider
  const shouldShowArrows = !shouldUseScrollSlider && showArrowsAtBreakpoint && slideCount > visibleSlides

  // Global keyboard navigation - works even when carousel isn't focused
  // Can be disabled via props to prevent conflicts with multiple carousels
  useEffect(() => {
    if (!enableGlobalKeyboard) return

    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      // Don't interfere if user is typing in form inputs
      const activeElement = document.activeElement
      if (
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.tagName === 'SELECT' ||
        (activeElement as HTMLElement)?.isContentEditable
      ) {
        return
      }

      if (event.key === 'ArrowLeft' && !isAtStart) {
        event.preventDefault()
        goToPrev()
      } else if (event.key === 'ArrowRight' && !isAtEnd) {
        event.preventDefault()
        goToNext()
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => document.removeEventListener('keydown', handleGlobalKeyDown)
  }, [isAtStart, isAtEnd, goToPrev, goToNext, enableGlobalKeyboard])

  const sliderContainerClasses = cx(
    styles.sliderContainer,
    !hasAnimated && styles.initialLoad,
    isAnimatingIn && styles.animateIn,
    sliderContainerClass && sliderContainerClass
  )
  // Fallback for no slides
  if (!slides || !Array.isArray(slides) || slideCount === 0) {
    return (
      <div className={cx(styles.carouselV2, className)} role='region' aria-label='No events available'>
        <p className={styles.noEventsMessage}>No events to display at this time.</p>
      </div>
    )
  }

  const hasTopNavCta = showAllShowsButton && allShowsUrl
  const shouldShowArrowNav = shouldShowArrows && !useDotArrows
  const shouldShowTopNavigation = hasTopNavCta || headline || shouldShowArrowNav
  // Prevent headline centering below 768 when inline nav arrows are present as it creates visual issues
  const headlineAlignment = isBelowSm && shouldShowArrowNav ? 'left' : headline?.justify

  return (
    <div
      ref={containerRef}
      className={cx(
        styles.carouselV2,
        className,
        useCustomSlideCalculation && styles['useCustomSlideCalculation'],
        shouldPeekNextSlide && styles['shouldPeekNextSlide'],
        fadePeekingSlides && styles['shouldFadePeekingSlides']
      )}
      aria-label='Event carousel'
      role='region'
    >
      {shouldShowTopNavigation && (
        <div
          className={cx(styles.topNavigation, {
            [styles.hasButton]: hasTopNavCta,
            [styles.hasHeadline]: Boolean(headline),
            [styles.headlineIsCentered]: headlineAlignment === 'center',
            [styles.hasArrowNav]: Boolean(shouldShowArrowNav)
          })}
        >
          {hasTopNavCta && (
            <Link href={allShowsUrl} className={styles.buttonWrapper} aria-label='View all shows' tabIndex={0}>
              All shows
              <svg width='7' height='12' viewBox='0 0 7 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M5.37043 6.01086L0 0.618685L0.615462 0L6.60458 6.01086L0.615462 12L0 11.3813L5.37043 6.01086Z'
                  fill='currentColor'
                />
              </svg>
            </Link>
          )}
          {headline && (
            <Heading
              level={headline.level}
              justify={headlineAlignment}
              levelDisplay={headline.levelDisplay}
              noMarginBottom
              noMarginTop
            >
              {headline.title}
            </Heading>
          )}
          {shouldShowArrowNav && (
            <div className={styles.arrows}>
              <CarouselArrows
                shouldShowArrows={true}
                goToPrev={goToPrev}
                goToNext={goToNext}
                isAtStart={isAtStart}
                isAtEnd={isAtEnd}
                currentSlide={currentSlide}
                slideCount={slideCount}
                goToSlide={goToSlide}
              />
            </div>
          )}
        </div>
      )}
      <div className={sliderContainerClasses}>
        {shouldUseScrollSlider ? (
          <ScrollSlider className={styles.scrollSliderWrapper}>
            {slides.map((slide, index) => {
              return renderSlide(slide, index, isTransitioning ?? false)
            })}
          </ScrollSlider>
        ) : (
          <Slider ref={sliderRef} {...settings}>
            {slides.map((slide, index) => {
              return renderSlide(slide, index, isTransitioning ?? false)
            })}
          </Slider>
        )}
      </div>
      {useDotArrows && (
        <div className={styles.bottomArrows}>
          <CarouselArrows
            shouldShowArrows={Boolean(shouldShowArrows)}
            goToPrev={goToPrev}
            goToNext={goToNext}
            isAtStart={isAtStart}
            isAtEnd={isAtEnd}
            currentSlide={currentSlide}
            slideCount={slideCount}
            useCustomDots={true}
            goToSlide={goToSlide}
          />
        </div>
      )}
    </div>
  )
}

export default CarouselV2
